import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FiPlus, FiEdit2, FiTrash2, FiEye, FiSearch } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { toast, confirmDelete, showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import BulkActions from '../../components/common/BulkActions';
import Pagination from '../../components/common/Pagination';
import StatusBadge from '../../components/common/StatusBadge';
import { useAuth } from '../../context/AuthContext';
import { formatMoney } from '../../utils/format';
import SearchableSelect from '../../components/common/SearchableSelect';

export default function Employees() {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [params, setParams] = useState({ page: 1, search: '', employee_type: '', per_page: 100 });
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.employees.list(params);
      const list = Array.isArray(data.data) ? data.data : data.employees || data;
      setRows(Array.isArray(list) ? list : list.data || []);
      setMeta(data.data ? { current_page: data.current_page, last_page: data.last_page, total: data.total, per_page: data.per_page, per_page_options: data.per_page_options } : null);
    } catch (err) {
      showError('Failed to load employees', extractError(err));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (row) => {
    const res = await confirmDelete(`employee "${row.name}"`);
    if (!res.isConfirmed) return;
    try {
      await api.employees.remove(row.id);
      toast('success', '', 'Employee deleted');
      load();
    } catch (err) {
      showError('Failed to delete employee', extractError(err));
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="Employees"
        subtitle="Helper, operator and staff records."
        crumbs={[{ label: 'HR' }, { label: 'Employees' }]}
        actions={hasPermission('create-employees') && (
          <button className="btn btn-primary" onClick={() => navigate('/employees/new')}><FiPlus /> Add Employee</button>
        )}
      />

      <div className="card">
        <div className="card-body">
          <div className="filters-bar">
            <div className="topbar-search">
              <FiSearch className="search-icon" />
              <input placeholder="Search employees..." value={params.search} onChange={(e) => setParams({ ...params, search: e.target.value, page: 1 })} />
            </div>
            <SearchableSelect options={[{ value: '', label: 'All Types' }, { value: 'operator', label: 'Operator' }, { value: 'helper', label: 'Helper' }, { value: 'staff', label: 'Staff' }]} value={params.employee_type} onChange={(v) => setParams({ ...params, employee_type: v, page: 1 })} placeholder="All Types" />
          </div>

          <BulkActions selectedIds={selectedIds} onClear={() => setSelectedIds([])} onDone={load} deleteType="employees" label="employees" />
          <DataTable
            loading={loading}
            data={rows}
            emptyTitle="No employees found"
            selectable
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            columns={[
              { key: 'name', label: 'Employee', render: (r) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div className="user-avatar" style={{ width: 32, height: 32, fontSize: 13, borderRadius: 8 }}>
                    {r.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600 }}>{r.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.phone || '-'}</div>
                  </div>
                </div>
              ) },
              { key: 'machine_id', label: 'Machine ID', render: (r) => r.machine_id || '-' },
              { key: 'employee_type', label: 'Type', render: (r) => <StatusBadge status={r.employee_type} /> },
              { key: 'designation', label: 'Designation' },
              { key: 'basic_salary', label: 'Basic Salary', align: 'right', render: (r) => formatMoney(r.basic_salary) },
              { key: 'joining_date', label: 'Joining Date', render: (r) => r.joining_date ? String(r.joining_date).slice(0, 10) : '-' },
              { key: 'is_active', label: 'Status', render: (r) => <StatusBadge status={r.is_active ? 'active' : 'inactive'} /> },
              { key: 'id', label: '', align: 'right', render: (r) => (
                <div className="actions">
                  <Link to={`/employees/${r.id}`} className="btn-icon"><FiEye /></Link>
                  {hasPermission('update-employees') && <button className="btn-icon" onClick={() => navigate(`/employees/${r.id}/edit`)}><FiEdit2 /></button>}
                  {hasPermission('delete-employees') && <button className="btn-icon danger" onClick={() => handleDelete(r)}><FiTrash2 /></button>}
                </div>
              ) },
            ]}
          />
          {meta && <Pagination meta={meta} onPage={(p) => setParams({ ...params, page: p })} onPerPage={(n) => setParams({ ...params, per_page: n, page: 1 })} />}
        </div>
      </div>
    </div>
  );
}
